import React from 'react';

interface AnalysisProgressProps {
  progress: number; // 0-100
  message?: string;
}

const AnalysisProgress: React.FC<AnalysisProgressProps> = ({ progress, message }) => {
  // 限制进度在0-100之间
  const clampedProgress = Math.max(0, Math.min(100, progress));
  
  // 根据进度百分比判断当前阶段（用于显示对应的小人图片）
  const getCurrentStage = () => {
    // 根据进度百分比准确匹配阶段
    if (clampedProgress < 40) {
      // 0-39%: 搜索阶段
      return 'search';
    } else if (clampedProgress < 60) {
      // 40-59%: AI分析阶段
      return 'analyze';
    } else {
      // 60-100%: 可视化阶段（生成传导链条、绘制传导图、生成分析报告）
      return 'visualize';
    }
  };

  // 根据进度百分比获取对应的消息文本
  const getDisplayMessage = () => {
    // 根据进度百分比准确匹配消息
    if (clampedProgress < 10) {
      return message && message.trim() ? message : '准备开始分析...';
    } else if (clampedProgress < 40) {
      // 10%-39%: 搜索实时信息...
      return '搜索实时信息...';
    } else if (clampedProgress < 60) {
      // 40%-59%: AI 分析中...
      return 'AI 分析中...';
    } else if (clampedProgress < 75) {
      // 60%-74%: 生成传导链条...
      return '生成传导链条...';
    } else if (clampedProgress < 90) {
      // 75%-89%: 绘制传导图...
      return '绘制传导图...';
    } else if (clampedProgress < 100) {
      // 90%-99%: 生成分析报告...
      return '生成分析报告...';
    } else {
      // 100%: 分析完成！
      return '分析完成！';
    }
  };

  const currentStage = getCurrentStage();
  const displayMessage = getDisplayMessage();

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="max-w-2xl mx-auto">
        {/* 标题 */}
        <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">正在分析中...</h3>
        
        {/* 进度条容器 */}
        <div className="relative mb-4">
          {/* 进度条背景 */}
          <div className="w-full h-8 bg-gray-200 rounded-full overflow-hidden shadow-inner">
            {/* 进度条填充 */}
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
              style={{ width: `${clampedProgress}%` }}
            >
              {/* 进度条动画效果 */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
            </div>
          </div>
          
          {/* 小人容器 - 位置随进度移动 */}
          <div
            className="absolute top-0 transition-all duration-300 ease-out pointer-events-none"
            style={{
              left: `calc(${clampedProgress}% - 64px)`, // 居中，图片更大所以偏移更多
              transform: 'translateY(-32px)'
            }}
          >
            {/* 根据阶段显示不同的图片 */}
            {currentStage === 'search' && (
              // 第一阶段：搜索实时信息
              <img
                src="/images/search-stage.png"
                alt="搜索实时信息"
                className="w-32 h-32 drop-shadow-lg"
                style={{ imageRendering: 'auto' }}
              />
            )}
            
            {currentStage === 'analyze' && (
              // 第二阶段：AI 分析中
              <img
                src="/images/analyze-stage.png"
                alt="AI 分析中"
                className="w-32 h-32 drop-shadow-lg"
                style={{ imageRendering: 'auto' }}
              />
            )}
            
            {currentStage === 'visualize' && (
              // 第三阶段：绘制传导图
              <img
                src="/images/visualize-stage.png"
                alt="绘制传导图"
                className="w-32 h-32 drop-shadow-lg"
                style={{ imageRendering: 'auto' }}
              />
            )}
          </div>
        </div>
        
        {/* 进度百分比和消息 */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 font-medium">{displayMessage}</span>
          <span className="text-blue-600 font-bold text-lg">{Math.round(clampedProgress)}%</span>
        </div>
      </div>
      
      {/* CSS动画样式 */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default AnalysisProgress;
